import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Box, Button, CircularProgress, Icon, Stack, Typography } from '@mui/material'
import { jwtDecode } from 'jwt-decode'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../../Shared/Contexts/AuthContext'
import { useTests } from '../Contexts/TestsContext'

const TestGateway = ({ tracker, gateway, setResult = () => { } }) => {
  const { authState, currentUser } = useAuth()
  const { useSaveGatewayTest, usePollDevicesMeasures, useGetTestConfig } = useTests()
  const navigate = useNavigate()

  const { email } = jwtDecode(authState.accessToken)

  const [stopPolling, setStopPolling] = useState(false)
  const [timeoutId, setTimeoutId] = useState(null)
  const [intervalId, setIntervalId] = useState(null)
  const [measuresNumber, setMeasuresNumber] = useState(0)
  const [testFailure, setTestFailure] = useState(false)
  const [success, setSuccess] = useState(false)
  const [startTest] = useState(new Date().toISOString())
  const [testResult, setTestResult] = useState(null)
  const [testLimits, setTestLimits] = useState(null)
  const [successColor, setSuccessColor] = useState('success')

  const pollFunction = () => {
    usePollDevicesMeasures(startTest, tracker.id).then((res) => {
      if (tracker.selectedTech == 'wmbus') {
        const validMeasures = res.data.filter((x) => x.payload.dc == gateway.wmbusId)

        setTestResult(validMeasures[0])
        setMeasuresNumber(validMeasures.length)

        if (validMeasures.length >= 3) {
          setStopPolling(true)
          res.data[0].payload.rssi > Number(testLimits.rssi) ? setSuccessColor('success') : setSuccessColor('warning')
        }
      } else {
        const validMeasures = res.data.filter((x) => x.linkStatus.gateways.some((g) => g.mac == gateway.gatewayId))
          .map((x) => ({
            ...x,
            linkStatus: {
              ...x.linkStatus,
              gateways: x.linkStatus.gateways.filter((g) => g.mac == gateway.gatewayId)
            }
          }))

        setTestResult(validMeasures[0])
        setMeasuresNumber(validMeasures.length)

        if (validMeasures.length >= 3) {
          if (validMeasures[0].linkStatus.gateways[0].rssi > Number(testLimits.rssi) || validMeasures[0].linkStatus.gateways[0].snr > Number(testLimits.snr)) {
            setSuccessColor('success')
          }
          else {
            setSuccessColor('warning')
          }
          setStopPolling(true)
        }
      }
    })
  }

  const handleBackToHome = () => {
    navigate('/select-test')
  }

  useEffect(() => {
    const POLLING_INTERVAL = 10000   // 10 secondi
    const TOTAL_DURATION = 300000   // 5 minuti

    if (tracker == null || gateway == null || testLimits == null) {
      return
    }

    const intervalId = setInterval(pollFunction, POLLING_INTERVAL)

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId)
      setTestFailure(true)
    }, TOTAL_DURATION)

    setIntervalId(intervalId)
    setTimeoutId(timeoutId)
  }, [tracker, gateway, testLimits])

  useEffect(() => {
    if (stopPolling && intervalId && timeoutId && (testResult || testFailure)) {
      setStopPolling(false)
      clearInterval(intervalId)
      clearTimeout(timeoutId)

      const payload = {
        timestamp: new Date().toISOString(),
        snr: tracker.selectedTech == 'wmbus' ? testResult?.payload.snr : testResult?.linkStatus.gateways[0].snr,
        rssi: tracker.selectedTech == 'wmbus' ? testResult?.payload.rssi : testResult?.linkStatus.gateways[0].rssi,
        fieldTestDevice: {
          deviceId: tracker.id,
          name: tracker.name,
          organizationKey: currentUser.currentOrganizationKey
        },
        operator: {
          user: authState.name,
          email: email
        }
      }

      useSaveGatewayTest(payload, gateway.serial).then((res) => {
        setSuccess(true)
        setResult(res.data[0])
      })
    }
  }, [stopPolling, testResult])

  useEffect(() => {
    useGetTestConfig().then((res) => {
      setTestLimits(res.data)
    })
  }, [])

  return testFailure ? (
    <Stack
      display="flex"
      flexDirection="column"
      marginTop={5}
    >
      <Typography variant="h4" textAlign={'center'} marginBottom={5}>
        Test fallito
      </Typography>

      <Button onClick={handleBackToHome} variant='outlined' sx={{ marginTop: '10px', marginBottom: '5px' }}>Torna alla home</Button>
    </Stack>
  ) : (
    <Stack
      display="flex"
      flexDirection="column"
      marginTop={5}
    >
      <Typography variant="h4" textAlign={'center'} marginBottom={5}>
        {success ? 'Test completato con successo!' : 'In corso...'}
      </Typography>

      <Typography variant="body1" textAlign={'center'} marginBottom={2}>
        Controlla il Tester e attendi l&apos;invio dei pacchetti
      </Typography>

      <Box display='flex' justifyContent='center' marginBottom={5}>
        {success ? <Icon color={successColor} sx={{ fontSize: '8rem' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: '8rem' }} />
        </Icon> : <CircularProgress size={'5rem'} />}
      </Box>

      <Typography variant="body1" textAlign={'left'} fontWeight={'bold'} marginBottom={'5px'} id="recieved-mesures">
        {measuresNumber} misure ricevute
      </Typography>

      <Typography variant="body2" textAlign={'left'} fontWeight={'bold'}>
        Ultimo uplink
      </Typography>
      <Box
        display="flex"
        justifyContent="space-around"
      >
        <Typography variant="body2" textAlign={'left'}>
          device
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          snr
        </Typography>
        <Typography variant="body2" textAlign={'left'}>
          rssi
        </Typography>
      </Box>
      <Box
        display="flex"
        justifyContent="space-around"
        backgroundColor={'#e0e0e0'}
        borderRadius={'10px'}
      >
        {
          tracker.selectedTech == 'wmbus' ?
            <>
              <Typography variant="body2" textAlign={'left'}>
                {testResult?.deviceId ? tracker.name : '-'}
              </Typography>
              <Typography variant="body2" textAlign={'left'}>
                {testResult?.payload.snr ? testResult?.payload.snr : '-'}
              </Typography>
              <Typography variant="body2" textAlign={'left'}>
                {testResult?.payload.rssi ? testResult?.payload.rssi : '-'}
              </Typography>
            </> :
            <>
              <Typography variant="body2" textAlign={'left'}>
                {testResult?.deviceId ? tracker.name : '-'}
              </Typography>
              <Typography variant="body2" textAlign={'left'}>
                {testResult?.linkStatus.gateways[0].snr ? testResult?.linkStatus.gateways[0].snr : '-'}
              </Typography>
              <Typography variant="body2" textAlign={'left'}>
                {testResult?.linkStatus.gateways[0].rssi ? testResult?.linkStatus.gateways[0].rssi : '-'}
              </Typography>
            </>
        }

      </Box>
    </Stack>
  )
}

export default TestGateway